import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { InteractionManager, Platform, AppState } from 'react-native';

import { useAuth } from './AuthContext';
import { navigationRef } from '../core/navigation/navigationRef';
import { completeLoginNotificationPrompt } from '../services/loginNotificationPromptGate';
import PushNotificationService from '../services/pushNotificationService';
import type { PushPermissionStatus } from '../services/pushNotificationTypes';

type NotificationContextType = {
  isInitialized: boolean;
  deviceToken: string | null;
  areNotificationsEnabled: boolean;
  isRegistered: boolean;
  permissionStatus: PushPermissionStatus;
  pushOptedIn: boolean;
  devicePushOn: boolean;
  registerDeviceToken: () => Promise<boolean>;
  enablePush: () => Promise<boolean>;
  disablePush: () => Promise<boolean>;
  refreshPermission: () => Promise<void>;
  openSystemNotificationSettings: () => Promise<void>;
  subscribeToTopic: (topic: string) => Promise<boolean>;
  unsubscribeFromTopic: (topic: string) => Promise<boolean>;
};

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export function NotificationProvider({ children }: PropsWithChildren) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [deviceToken, setDeviceToken] = useState<string | null>(null);
  const [areNotificationsEnabled, setAreNotificationsEnabled] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<PushPermissionStatus>('undetermined');
  const [pushOptedIn, setPushOptedIn] = useState(true);
  const { user, isAuthenticated, isLoading: authLoading, tokens } = useAuth();
  const notificationService = PushNotificationService.getInstance();
  const authHydratedRef = useRef(false);
  const wasAuthenticatedRef = useRef(false);
  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;

  useEffect(() => {
    void initializeNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (!authHydratedRef.current) {
      authHydratedRef.current = true;
      wasAuthenticatedRef.current = isAuthenticated;
      return;
    }

    const justLoggedIn = isAuthenticated && !wasAuthenticatedRef.current;
    wasAuthenticatedRef.current = isAuthenticated;
    if (!justLoggedIn) {
      return;
    }

    if (Platform.OS === 'web') {
      completeLoginNotificationPrompt();
      return;
    }

    void promptAfterLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated]);

  useEffect(() => {
    if (!isInitialized || !isAuthenticated || !user || !tokens?.accessToken) {
      if (!isInitialized || !notificationService.hasRegisterableToken()) {
        setIsRegistered(false);
      }
      return;
    }
    if (!notificationService.isPushOptedIn()) {
      setIsRegistered(false);
      return;
    }
    if (!notificationService.hasRegisterableToken()) {
      return;
    }
    void registerDeviceToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, isAuthenticated, user?.id, tokens?.accessToken, deviceToken]);

  useEffect(() => {
    const handleTokenRefresh = async () => {
      const newToken = notificationService.getDeviceToken();
      if (newToken) {
        setDeviceToken(newToken);
      }
      if (isAuthenticated && user && tokens?.accessToken && notificationService.hasRegisterableToken()) {
        await registerDeviceToken();
      }
    };

    notificationService.onTokenRefresh(() => {
      void handleTokenRefresh();
    });

    return () => {
      notificationService.onTokenRefresh(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id, tokens?.accessToken]);

  const initializeNotifications = async () => {
    try {
      await notificationService.initialize();
      const token = readDeviceToken();
      const enabled = await notificationService.areNotificationsEnabled();
      const status = await notificationService.getPermissionStatus();
      setDeviceToken(token);
      setAreNotificationsEnabled(enabled);
      setPermissionStatus(status);
      setPushOptedIn(notificationService.isPushOptedIn());
      setIsInitialized(true);
    } catch (error) {
      console.error('[NotificationContext] Failed to initialize notifications', error);
    }
  };

  const readDeviceToken = () =>
    Platform.OS === 'ios'
      ? notificationService.getAPNSToken() ?? notificationService.getDeviceToken()
      : notificationService.getDeviceToken();

  const promptAfterLogin = async () => {
    try {
      await new Promise<void>((resolve) => {
        InteractionManager.runAfterInteractions(() => resolve());
      });

      const deadline = Date.now() + 4000;
      while (Date.now() < deadline) {
        if (navigationRef.isReady()) {
          const root = navigationRef.getRootState();
          const name = root?.routes[root.index]?.name;
          if (name === 'MainTabs') {
            break;
          }
        }
        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      // Auth is a transparent modal — presenting the iOS permission alert
      // while it is still dismissing freezes the window.
      await new Promise((resolve) => setTimeout(resolve, 450));

      if (!isAuthenticatedRef.current) {
        return;
      }

      const enabled = await notificationService.requestPermissionIfNeeded();
      setDeviceToken(readDeviceToken());
      setAreNotificationsEnabled(enabled);
      setPermissionStatus(await notificationService.getPermissionStatus());
      setPushOptedIn(notificationService.isPushOptedIn());
    } catch (error) {
      console.error('[NotificationContext] Failed to request notification permission after login', error);
    } finally {
      completeLoginNotificationPrompt();
    }
  };

  const refreshPermission = useCallback(async () => {
    const enabled = await notificationService.areNotificationsEnabled();
    const status = await notificationService.getPermissionStatus();
    setAreNotificationsEnabled(enabled);
    setPermissionStatus(status);
    setPushOptedIn(notificationService.isPushOptedIn());
    if (enabled && notificationService.isPushOptedIn()) {
      setDeviceToken(readDeviceToken());
    }
  }, [notificationService]);

  useEffect(() => {
    const sub = AppState.addEventListener('change', (next) => {
      if (next === 'active') {
        void refreshPermission();
      }
    });
    return () => sub.remove();
  }, [refreshPermission]);

  const enablePush = useCallback(async (): Promise<boolean> => {
    const status = await notificationService.getPermissionStatus();
    if (status === 'blocked') {
      await notificationService.openSystemNotificationSettings();
      await refreshPermission();
      return false;
    }
    const enabled = await notificationService.requestPermissionIfNeeded({ force: true });
    setDeviceToken(readDeviceToken());
    setAreNotificationsEnabled(enabled);
    setPermissionStatus(await notificationService.getPermissionStatus());
    setPushOptedIn(notificationService.isPushOptedIn());
    if (!enabled) {
      setIsRegistered(false);
      return false;
    }
    if (!isAuthenticated || !user || !tokens?.accessToken) {
      return enabled;
    }
    const success = await notificationService.saveTokenToBackend();
    setIsRegistered(success);
    return success;
  }, [isAuthenticated, notificationService, refreshPermission, tokens?.accessToken, user]);

  const disablePush = useCallback(async (): Promise<boolean> => {
    await notificationService.setPushOptedIn(false);
    const unregistered = await notificationService.unregisterFromBackend();
    setPushOptedIn(false);
    setIsRegistered(false);
    return unregistered;
  }, [notificationService]);

  const openSystemNotificationSettings = useCallback(async () => {
    await notificationService.openSystemNotificationSettings();
  }, [notificationService]);

  const registerDeviceToken = useCallback(async (): Promise<boolean> => {
    if (!isAuthenticated || !user || !tokens?.accessToken) {
      setIsRegistered(false);
      return false;
    }
    if (!notificationService.isPushOptedIn()) {
      setIsRegistered(false);
      return false;
    }
    if (!notificationService.hasRegisterableToken()) {
      setIsRegistered(false);
      return false;
    }

    try {
      const tokenChanged = await notificationService.hasTokenChanged();
      if (!tokenChanged && isRegistered) {
        return true;
      }

      const success = await notificationService.saveTokenToBackend();
      setIsRegistered(success);
      return success;
    } catch (error) {
      console.error('[NotificationContext] Error registering device token', error);
      setIsRegistered(false);
      return false;
    }
  }, [isAuthenticated, user, tokens?.accessToken, isRegistered, notificationService]);

  const subscribeToTopic = async (topic: string): Promise<boolean> => {
    try {
      return await notificationService.subscribeToTopic(topic);
    } catch (error) {
      console.error('[NotificationContext] Failed to subscribe to topic', error);
      return false;
    }
  };

  const unsubscribeFromTopic = async (topic: string): Promise<boolean> => {
    try {
      return await notificationService.unsubscribeFromTopic(topic);
    } catch (error) {
      console.error('[NotificationContext] Failed to unsubscribe from topic', error);
      return false;
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        isInitialized,
        deviceToken,
        areNotificationsEnabled,
        isRegistered,
        permissionStatus,
        pushOptedIn,
        devicePushOn: areNotificationsEnabled && pushOptedIn,
        registerDeviceToken,
        enablePush,
        disablePush,
        refreshPermission,
        openSystemNotificationSettings,
        subscribeToTopic,
        unsubscribeFromTopic,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}
